import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { adIntelIsOpen } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { usePreloadedHook } from "app/pages/digital-marketing/monitor-and-protect/shared/hooks/preloaded-hook";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { useMonitorAndProtectCampaigns } from "../../../services/api/campaign";
import { UsageLimitLabel, UsageLimitLabelSkeleton } from "../../ui/usage-limit-label";
import { UpgradeButton } from "../upgrade-button";
import { UsageAndLimitsModal } from "../usage-and-limits-modal";

import { useLimits } from "./use-limits";

const CampaignsLimitLabel = () => {
    const translate = useTranslation();
    const { data: campaigns, isLoading, isError } = useMonitorAndProtectCampaigns({ includeDemoCampaigns: false });
    const used = campaigns?.length ?? 0;
    const campaignsLimit = swSettings.components?.MonitorAndProtectMaster?.resources?.MaximumAccountCampaigns ?? 0;
    const isUnlimited = campaignsLimit == -1;

    if (isError) {
        return null;
    }

    if (isLoading) {
        return <UsageLimitLabelSkeleton />;
    }

    return (
        <Text
            fontVariant="input2"
            color={colorsPalettes.carbon[500]}
        >
            {translate("monitor_and_protect.limits.campaigns_label")}
            {": "}
            <b>
                {isUnlimited
                    ? translate("monitor_and_protect.limits.unlimited")
                    : `${used}/${campaignsLimit.toLocaleString()}`}
            </b>
        </Text>
    );
};

export const LimitsBanner = () => {
    const translate = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const crawls = useLimits();
    const { data: campaigns } = useMonitorAndProtectCampaigns({ includeDemoCampaigns: false });
    const campaignsLimit = swSettings.components?.MonitorAndProtectMaster?.resources?.MaximumAccountCampaigns ?? 0;
    const campaignsUsed = campaigns?.length ?? 0;
    const campaignsAvailable = Math.max(0, campaignsLimit - campaignsUsed);

    const isNoTouchUser = isNoTouch(swSettings);
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();
    const { openHook } = usePreloadedHook();

    const handlePackageDetailsClick = () => setIsModalOpen(true);
    const handleClose = () => setIsModalOpen(false);
    const handleGetMoreCrawls = () => {
        setIsModalOpen(false);
        handlePurchaseAddonClick(ADDON_TYPE_NAME_DICT.MonitorAndProtectCrawls);
    };
    const handleUpgradePackage = () => {
        setIsModalOpen(false);
        openHook("MonitorAndProtectUnlock", "v5");
    };

    if (!adIntelIsOpen()) {
        return null;
    }

    return (
        <>
            <Flex
                alignItems="center"
                borderRadius="4px"
                border={`1px solid ${colorsPalettes.bluegrey["400"]}`}
                padding="2px 16px"
                width="100%"
                justifyContent="space-between"
            >
                <Flex
                    gap="24px"
                    alignItems="center"
                >
                    <Text fontVariant="subtitle2">{translate("monitor_and_protect.limits.account_balance")}</Text>
                    <CampaignsLimitLabel />
                    <UsageLimitLabel mode="all-campaigns" />
                </Flex>
                {isNoTouchUser ? (
                    <Button
                        type="flat"
                        onClick={handlePackageDetailsClick}
                        data-automation="mnp-limits-banner-package-details"
                    >
                        {translate("monitor_and_protect.limits.package_details_button")}
                    </Button>
                ) : (
                    <UpgradeButton type="flat" />
                )}
            </Flex>

            <UsageAndLimitsModal
                isOpen={isModalOpen}
                onClose={handleClose}
                crawls={{
                    used: crawls.used,
                    total: crawls.total,
                    available: crawls.available,
                }}
                campaigns={{
                    used: campaignsUsed,
                    total: campaignsLimit,
                    available: campaignsLimit === -1 ? -1 : campaignsAvailable,
                }}
                onGetMoreCrawls={handleGetMoreCrawls}
                onUpgradePackage={handleUpgradePackage}
            />
        </>
    );
};
